/**
 * Mode rafale : interrupteur mémorisé par appareil (coupé par défaut), session gardée sur
 * l'appareil, file des écritures envoyées une à une avec la clé de chaque scan.
 * Une coupure réseau remet l'écriture en file, renvoyée telle quelle : comptée une seule fois.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { errMsg, networkError, type AppError } from '@/api/errors';
import { err, errOf, type Result } from '@/api/result';
import {
  findStockModeProductByScan,
  type StockModeProduct,
} from '@/api/stocks/inventory/stockModeApi';
import { addProductStockKeyed } from '@/api/stocks/manualStock';
import { BurstFlash, type FlashInfo } from '@/features/stock-mode/burst/BurstFlash';
import {
  addCorrection,
  addScan,
  addUnknown,
  cancelBurst,
  confirmOp,
  displayStock,
  dropOp,
  emptyBurst,
  isTransientError,
  removeUnknown,
  restoreBurst,
  type BurstState,
} from '@/features/stock-mode/burst/burstModel';
import { isCodeIgnored, syncIgnoredCodesUser } from '@/features/stock-mode/ignoredCodes';
import { scanFailed, scanNeutral, scanSucceeded } from '@/features/stock-mode/scanFeedback';
import { ScanNotice, type NoticeInfo } from '@/features/stock-mode/burst/ScanNotice';
import { useLocalStorageState } from '@/hooks/useLocalStorageState';
import { useEffectiveTenantId } from '@/hooks/useTenant';
import { isObviousNonProductScan } from '@/lib/scanFilter';
import { useAuth } from '@/contexts/AuthContext';

const SESSION_KEY = 'vp_burst_session';
const RETRY_MS = 2000;
const SEND_TIMEOUT_MS = 8000;
const LOOKUP_RETRIES = 4;
const LOOKUP_RETRY_MS = 1000;

export type BurstContextValue = {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  state: BurstState;
  /** Scan à compter +1 ; code inconnu : mis de côté avec un bip d'erreur. */
  scan: (code: string) => Promise<void>;
  correct: (product: StockModeProduct) => void;
  cancel: () => void;
  dismissUnknown: (code: string) => void;
  /** Montre en grand un stock qui vient de changer (aussi hors rafale). */
  flash: (info: FlashInfo) => void;
  /**
   * Filtre d'entrée de tout scan : code ignoré sur l'appareil ou QR code / texte.
   * Faux = rien d'autre à faire (bip neutre et bandeau déjà joués).
   */
  screen: (code: string) => boolean;
};

const BurstContext = createContext<BurstContextValue | null>(null);

const readSession = (tenantId: string | null): BurstState => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return restoreBurst(raw ? JSON.parse(raw) : null, tenantId);
  } catch {
    return emptyBurst(tenantId);
  }
};

const newKey = (): string => crypto.randomUUID();

/** Article lu une fois par code : une rafale du même code ne relit pas le serveur. */
function useProductLookup() {
  const cache = useRef(new Map<string, Promise<StockModeProduct | null | 'error'>>());
  return useCallback((tenantId: string, code: string) => {
    const key = `${tenantId}:${code}`;
    const known = cache.current.get(key);
    if (known) return known;
    const pending = findStockModeProductByScan(tenantId, code).then((result) => {
      if (result.ok) return result.value;
      console.warn('[rafale] lecture article en échec', errOf(result).type);
      return 'error' as const;
    });
    cache.current.set(key, pending);
    // Un échec ou un code inconnu se relit au scan suivant (article créé entre-temps).
    void pending.then((value) => {
      if (value === null || value === 'error') cache.current.delete(key);
    });
    return pending;
  }, []);
}

const TENANT_WAIT_MS = 10_000;

/** Attend que le commerce soit connu (100 ms par 100 ms, 10 s au plus). */
const waitForTenant = async (ref: { current: string | null | undefined }) => {
  for (let waited = 0; !ref.current && waited < TENANT_WAIT_MS; waited += 100) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return ref.current ?? null;
};

/** Envoie la tête de file, une écriture à la fois ; renvoie plus tard après une coupure. */
function useBurstSender(
  state: BurstState,
  setState: (update: (current: BurstState) => BurstState) => void,
) {
  const queryClient = useQueryClient();
  const sending = useRef(false);
  const [retryTick, setRetryTick] = useState(0);
  const head = state.queue[0];

  useEffect(() => {
    const online = () => setRetryTick((tick) => tick + 1);
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, []);

  useEffect(() => {
    if (!head || sending.current) return;
    sending.current = true;
    // Réponse qui n'arrive jamais (réseau qui décroche) : on renvoie la même écriture,
    // la clé du scan évite tout double comptage.
    const timeout = new Promise<Result<number, AppError>>((resolve) =>
      setTimeout(() => resolve(err(networkError(new Error('Délai dépassé')))), SEND_TIMEOUT_MS),
    );
    void Promise.race([
      addProductStockKeyed(head.productId, head.delta, head.key, head.kind, head.note),
      timeout,
    ]).then((result) => {
      sending.current = false;
      if (result.ok) {
        setState((current) => confirmOp(current, head.key, result.value));
        void queryClient.invalidateQueries({ queryKey: ['products'] });
        return;
      }
      if (isTransientError(errOf(result))) {
        setTimeout(() => setRetryTick((tick) => tick + 1), RETRY_MS);
        return;
      }
      toast.error(`Écriture refusée : ${errMsg(result)}`);
      setState((current) => dropOp(current, head.key));
    });
  }, [head, retryTick, setState, queryClient]);
}

export function BurstProvider({ children }: { children: ReactNode }) {
  const tenantId = useEffectiveTenantId();
  const { user } = useAuth();
  useEffect(() => syncIgnoredCodesUser(user?.id ?? null), [user?.id]);
  const [enabled, setEnabled] = useLocalStorageState<boolean>('vp_burst_mode', false);
  const [state, setStateRaw] = useState<BurstState>(() => readSession(tenantId));
  const [flashInfo, setFlashInfo] = useState<FlashInfo | null>(null);
  const lookup = useProductLookup();
  // Commerce courant lu au moment du scan : s'il disparaît un instant (session rafraîchie),
  // le scan attend son retour au lieu d'être perdu ou lu « inconnu ».
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;

  // Session relue seulement si le commerce change vraiment. Un commerce qui disparaît un
  // instant (session rafraîchie) ne doit pas remplacer la session en cours par celle du
  // stockage, en retard sur les derniers scans : c'était la cause des scans perdus en rafale
  // (seul le dernier scan restait compté).
  useEffect(() => {
    setStateRaw((current) =>
      !tenantId || current.tenantId === tenantId ? current : readSession(tenantId),
    );
  }, [tenantId]);
  const setState = useCallback((update: (current: BurstState) => BurstState) => {
    setStateRaw((current) => {
      const next = update(current);
      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify(next));
      } catch {
        // Stockage plein : la session reste en mémoire.
      }
      return next;
    });
  }, []);
  useBurstSender(state, setState);

  const [notice, setNotice] = useState<NoticeInfo | null>(null);
  const screen = useCallback((code: string) => {
    if (isCodeIgnored(code)) {
      scanNeutral();
      setNotice({ text: `Code ignoré : ${code}`, ms: 1000 });
      return false;
    }
    if (isObviousNonProductScan(code)) {
      scanNeutral();
      setNotice({ text: "QR code / ce n'est pas un code-barres produit", ms: 1500 });
      return false;
    }
    return true;
  }, []);

  const scan = useCallback(
    async (code: string) => {
      if (!screen(code)) return;
      const tenant = await waitForTenant(tenantRef);
      if (!tenant) {
        scanFailed();
        toast.error('Commerce non chargé : scan non lu, rescanne cet article.');
        return;
      }
      // Lecture en échec (réseau, appareil chargé) : on réessaie au lieu de perdre le scan.
      let found = await lookup(tenant, code);
      for (let attempt = 1; found === 'error' && attempt <= LOOKUP_RETRIES; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, LOOKUP_RETRY_MS * attempt));
        found = await lookup(tenant, code);
      }
      if (found === 'error') {
        scanFailed();
        toast.error('Réseau indisponible : scan non lu, rescanne cet article.');
        return;
      }
      if (!found) {
        scanFailed();
        setState((current) => addUnknown(current, code));
        setFlashInfo({ unknownCode: code });
        return;
      }
      scanSucceeded();
      setState((current) => {
        const next = addScan(current, found, newKey());
        const line = next.lines[0];
        setFlashInfo({ name: found.name, stock: displayStock(line, next.queue), delta: 1 });
        return next;
      });
    },
    [lookup, setState, screen],
  );

  const value = useMemo<BurstContextValue>(
    () => ({
      enabled,
      setEnabled: (next: boolean) => {
        setEnabled(next);
        // Rappel discret à l'activation, au lieu d'un texte permanent à l'écran.
        if (next) setNotice({ text: 'Rafale activée · +1 par scan', ms: 2000 });
      },
      state,
      scan,
      correct: (product) => setState((current) => addCorrection(current, product, -1, newKey())),
      cancel: () => setState((current) => cancelBurst(current, newKey)),
      dismissUnknown: (code) => setState((current) => removeUnknown(current, code)),
      flash: setFlashInfo,
      screen,
    }),
    [enabled, setEnabled, state, scan, setState, screen],
  );

  return (
    <BurstContext.Provider value={value}>
      {children}
      <BurstFlash info={flashInfo} />
      <ScanNotice notice={notice} />
    </BurstContext.Provider>
  );
}

export function useBurst(): BurstContextValue {
  const context = useContext(BurstContext);
  if (!context) throw new Error('useBurst doit être utilisé dans un <BurstProvider>');
  return context;
}

/** Sans provider (écran hors cadre), le mode rafale est absent. */
export function useOptionalBurst(): BurstContextValue | null {
  return useContext(BurstContext);
}
